import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getUuid } from 'utils/uuid'
import { GET } from 'rest/request'
import * as urls from 'rest/urls'
import { RootState } from 'store'
import { v7 } from 'uuid'

export const getMessages = createAsyncThunk<chat.ChatResponse>('chat/getMessages', async () => {
  return GET(urls.mycallservices)
})

export const getNotifications = createAsyncThunk<response.DataResponse<chat.Notification[]>, string | undefined>('chat/getNotifications', async (nextPage) => {
  return GET(nextPage || urls.usernotifications)
})

const getUnreadNotifications = (notifications: chat.Notification[]) => {
  return notifications.reduce((r, n) => {
    if (!n.read_date) r += 1
    return r
  }, 0)
}

const initialState: chat.InitialState = {
  unreadNotifications: 0,
  notifications: [],
  threads: {}
}

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addChatNotification: (state, action: PayloadAction<chat.Notification>) => {
      const chatId = action.payload.chatId
      const notifications = [action.payload, ...state.notifications.filter(n => chatId ? n.chatId !== chatId : n.id !== action.payload.id)]
      state.unreadNotifications = getUnreadNotifications(notifications)
      state.notifications = notifications
    },
    updateChatNotification: (state, action: PayloadAction<{ id: string, values: Partial<chat.Notification> }>) => {
      const notifications = state.notifications.map(notification => notification.id === action.payload.id
        ? { ...notification, ...action.payload.values }
        : notification
      )
      state.unreadNotifications = getUnreadNotifications(notifications)
      state.notifications = notifications
    },
    removeChatNotification: (state, action: PayloadAction<{ id: string }>) => {
      const notifications = state.notifications.filter(notification => notification.id !== action.payload.id)
      state.unreadNotifications = getUnreadNotifications(notifications)
      state.notifications = notifications
    },
    addMessage: (state, action: PayloadAction<chat.NewMessagePayload & { isSender: boolean, files?: chat.NewFiles }>) => {
      const { _from, id = v7(), isSender, message, file, files, chat_id } = action.payload
      if (!_from || !chat_id) return

      // const previousNotification = state.notifications.find(n => n.chatId === chat_id)
      // const notification: chat.Notification = {
      //   id: previousNotification?.id || v7(),
      //   icon: previousNotification?.icon || 'service',
      //   type: previousNotification?.type || 'info',
      //   title: previousNotification?.title || 'Centrum pomocy',
      //   datetime: new Date().toJSON(),
      //   description: message,
      //   chatId: chat_id,
      //   action: previousNotification?.action || {
      //     type: 'redirect',
      //     path: `/chat/${chat_id}`
      //   }
      // }

      // state.notifications = [notification, ...state.notifications.filter(n => n.chatId !== chat_id)]
      // if (!previousNotification) {
      //   state.unreadNotifications = state.unreadNotifications + 1
      // }

      const newMessage: chat.ChatMessage = {
        id,
        object: 'Message',
        datetime: new Date().toJSON(),
        receiverRoom: isSender ? 'SERVICE' : '',
        senderUserId: _from.userID,
        threadId: chat_id,
        message,
      }

      if (files) {
        newMessage.documents = files
      } else if (file) {
        newMessage.documents = [file]
      }

      if (state.threads[chat_id]) {
        state.threads[chat_id].messages = [...state.threads[chat_id].messages, newMessage]
      } else {
        state.threads[chat_id] = {
          object: 'CallService',
          id: chat_id,
          name: '',
          type: 'chat',
          identifier: chat_id,
          userId: isSender ? _from.userID : '',
          serviceId: isSender ? '' : _from.userID,
          started_at: new Date().toJSON(),
          deviceIdentifier: getUuid(),
          messages: [newMessage],
          started_by: ''
        }
      }
    },
    updateMessage: (state, action: PayloadAction<{ chatId: string, messageId: string, values: Partial<chat.ChatMessage> }>) => {
      if (state.threads[action.payload.chatId]) {
        state.threads[action.payload.chatId].messages = state.threads[action.payload.chatId].messages.map(message => {
          return message.id === action.payload.messageId ? { ...message, ...action.payload.values } : message
        })
      }
    },
    closeThread: (state, action: PayloadAction<{ chatId: string }>) => {
      state.threads[action.payload.chatId].closed_at = new Date().toJSON()
    }
  },
  extraReducers(builder) {
    builder.addCase(getMessages.fulfilled, (state, action) => {
      if (Array.isArray(action.payload?.data)) {
        state.threads = action.payload.data.reduce((r: chat.Threads, d: chat.Chat) => {
          r[d.id] = d
          return r
        }, {} as chat.Threads)
      }
    })
    builder.addCase(getNotifications.fulfilled, (state, action) => {
      if (Array.isArray(action.payload?.data)) {
        const notifications = action.meta.arg
          ? [...state.notifications, ...action.payload.data]
          : action.payload.data
        state.unreadNotifications = getUnreadNotifications(notifications)
        state.notificationsMeta = action.payload.meta
        state.notifications = notifications
      }
    })
  }
})

export const { addMessage, updateMessage, addChatNotification, updateChatNotification, removeChatNotification, closeThread } = chatSlice.actions

export const selectChatThreads = (state: RootState) => state.chat.threads
export const selectChatNotifications = (state: RootState) => state.chat.notifications
export const selectChatNotificationsMeta = (state: RootState) => state.chat.notificationsMeta
export const selectChatThread = (state: RootState, threadId: string) => state.chat.threads[threadId] || {}
export const selectChatUnreadNotifications = (state: RootState) => state.chat.unreadNotifications

export default chatSlice.reducer